import { useEffect, useRef, useState } from 'react';

// Shared combobox keyboard + outside-click behavior for autocomplete inputs.
// Returns the state and handlers; the caller wires them onto its own input/listbox markup.
export function useCombobox(options, onSelect) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);

  // Reset highlight whenever the option set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [options]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && activeIndex < options.length) {
        e.preventDefault();
        onSelect(options[activeIndex]);
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActiveIndex(options.length > 0 ? 0 : -1);
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActiveIndex(options.length - 1);
    }
  };

  return { open, setOpen, activeIndex, setActiveIndex, containerRef, handleKeyDown };
}
